// 檢查是否為同一用戶的不同 ID
async function checkIfSameUser(userId1: string, userId2: string): Promise<boolean> {
  try {
    // 如果兩個 ID 相同，直接返回 true
    if (userId1 === userId2) {
      return true;
    }
    
    // 如果其中一個是 email 格式，檢查是否對應同一個用戶
    if (userId1.includes('@') && userId2.includes('@')) {
      return userId1 === userId2;
    }
    
    // 如果其中一個是 email，另一個是 UID，需要查詢映射關係
    if (userId1.includes('@') || userId2.includes('@')) {
      const email = userId1.includes('@') ? userId1 : userId2;
      const uid = userId1.includes('@') ? userId2 : userId1;
      
      // 這裡可以加入更複雜的映射邏輯
      // 目前先檢查是否為已知的映射關係
      const knownMappings: Record<string, string> = {
        'uc9C6sAfsPT743cQApZ4MmZVoU72': 'jeff@redball.com.tw',
        'jeff@redball.com.tw': 'uc9C6sAfsPT743cQApZ4MmZVoU72'
      };
      
      if (knownMappings[uid] === email || knownMappings[email] === uid) {
        return true;
      }
    }
    
    // 檢查是否為 guest 用戶的映射關係
    if (userId1.startsWith('guest_') && userId2.startsWith('guest_')) {
      // 兩個都是 guest 用戶，檢查是否為同一用戶的不同 session
      // 這裡可以加入更複雜的 guest 用戶識別邏輯
      return false; // 暫時不允許 guest 用戶之間的映射
    }
    
    return false;
  } catch (error) {
    console.error('[checkIfSameUser] 檢查用戶映射失敗:', error);
    return false;
  }
}

// 生成基於檔案名稱的文檔 ID
function generateFileBasedDocId(fileName: string, fileSize?: number): string {
  // 移除特殊字符，保留基本字符和中文字符
  const cleanFileName = fileName
    .replace(/[^a-zA-Z0-9\u4e00-\u9fff.-]/g, '_')
    .toLowerCase();
  
  // 如果有檔案大小，加入hash確保唯一性
  if (fileSize) {
    const hash = Math.abs(fileName.length + fileSize).toString(36);
    return `file_${cleanFileName}_${hash}`;
  }
  
  return `file_${cleanFileName}`;
}

// 生成複合文檔ID：{userId}_{fileId}_{version}
function generateCompositeDocId(
  fileId: string, 
  userId: string, 
  version: string = 'v1'
): string {
  // 清理用戶ID，移除特殊字符
  const cleanUserId = userId.replace(/[^a-zA-Z0-9@.-]/g, '_');
  
  // 生成複合文檔ID
  return `${cleanUserId}_${fileId}_${version}`;
}

// 版本管理：獲取下一個版本號
function getNextVersion(existingVersions: string[]): string {
  if (existingVersions.length === 0) return 'v1';
  
  const maxVersion = existingVersions
    .map(v => parseInt(v.replace('v', '')))
    .reduce((max, current) => Math.max(max, current), 0);
  
  return `v${maxVersion + 1}`;
}

// 生成安全的 Firestore 文檔 ID，使用分層設計
export function generateSafeDocId(
  videoId: string, 
  userId?: string, 
  fileName?: string, 
  fileSize?: number,
  version?: string
): string {
  // 🔥 修復：強制要求 userId，不再支持舊格式
  if (!userId) {
    console.error('❌ [generateSafeDocId] userId 為空，無法生成文檔 ID！', {
      videoId,
      fileName,
      fileSize,
      version,
      stack: new Error().stack
    });
    throw new Error('userId 為必填參數，無法生成文檔 ID');
    
    // 舊邏輯（已停用）：
    // if (videoId.match(/^[a-zA-Z0-9_-]{11}$/)) {
    //   return videoId;
    // }
  }
  
  // ✅ 新的分層設計：{userId}_{fileId}_{version}
  
  // 1. 處理 YouTube ID
  if (videoId.match(/^[a-zA-Z0-9_-]{11}$/)) {
    return generateCompositeDocId(videoId, userId, version || 'v1');
  }
  
  // 2. 處理 YouTube URL
  if (videoId.includes('youtube.com') || videoId.includes('youtu.be')) {
    const extractedId = extractYouTubeId(videoId);
    if (extractedId) {
      return generateCompositeDocId(extractedId, userId, version || 'v1');
    }
  }
  
  // 3. 處理檔案名稱
  if (fileName) {
    const fileBasedId = generateFileBasedDocId(fileName, fileSize);
    return generateCompositeDocId(fileBasedId, userId, version || 'v1');
  }
  
  // 4. 後備方案：基於路徑的hash
  let hash = 0;
  for (let i = 0; i < videoId.length; i++) {
    const char = videoId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const hashBasedId = `video_${Math.abs(hash).toString(36)}`;
  return generateCompositeDocId(hashBasedId, userId, version || 'v1');
}

// 匯入功能：選擇檔案並解析 JSON，onData 回傳解析結果
export function importVBIDsFromFile(onData: (data: any) => void) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json';
  input.onchange = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importData = JSON.parse(event.target?.result as string);
        if (importData && importData.checkpoints && Array.isArray(importData.checkpoints)) {
          onData(importData);
        } else {
          alert('匯入失敗，檔案格式錯誤');
        }
      } catch (err) {
        alert('匯入失敗，檔案格式錯誤');
      }
    };
    reader.readAsText(file);
  };
  input.click();
}

// 匯出功能：將資料轉成 JSON 檔案下載
export function exportVBIDsToFile(data: any, filename = 'checkpoints.json') {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// 新增：匯出影片的 Firestore 詳細資訊
export async function exportVideoFirestoreData(videoUrl: string, userId: string) {
  try {
    // 提取 videoId
    const videoId = extractYouTubeId(videoUrl) || videoUrl;
    
    // 查詢 sharedVbids 集合
    const q = query(
      collection(db, "sharedVbids"),
      where("userId", "==", userId),
      where("videoId", "==", videoId)
    );
    const querySnapshot = await getDocs(q);
    
    let firestoreData = null;
    if (!querySnapshot.empty) {
      const docData = querySnapshot.docs[0].data();
      firestoreData = {
        documentId: querySnapshot.docs[0].id,
        ...docData
      };
    }
    
    // ✅ 統一使用 sharedVbids，移除 favorites 集合查詢
    
    // 組合所有資料
    const exportData = {
      exportInfo: {
        timestamp: new Date().toISOString(),
        videoUrl: videoUrl,
        videoId: videoId,
        userId: userId,
        exportType: 'Firestore Debug Data'
      },
      sharedVbids: firestoreData,
      debugInfo: {
        hasSharedVbids: !!firestoreData,
        totalDocuments: querySnapshot.size
      }
    };
    
    // 匯出 JSON 檔案
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `firestore_debug_${videoId}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    console.log('✅ Firestore 資料匯出成功:', {
      videoId,
      userId,
      hasSharedVbids: !!firestoreData
    });
    
    return exportData;
  } catch (error) {
    console.error('❌ 匯出 Firestore 資料失敗:', error);
    throw error;
  }
}

import { db } from '../firebase';
import { addDoc, collection, query, where, getDocs, updateDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { vbidsToNumberKeys } from './numberKeyUtils';

// 產生唯一 guestId 並存 localStorage - 使用增強版管理系統
export function getGuestId() {
  try {
    // 使用增強版訪客管理系統（同步導入）
    const { enhancedGuestManager } = require('./enhancedGuestManager');
    const stats = enhancedGuestManager.getSessionStats();
    
    console.log('[getGuestId] 訪客會話統計:', {
      guestId: stats.guestId,
      sessionCount: stats.sessionCount,
      isNew: stats.isNew
    });
    
    return stats.guestId;
  } catch (error) {
    console.warn('[getGuestId] 增強版系統載入失敗，使用降級方案:', error);
    
    // 降級到原始方案
    let guestId = localStorage.getItem('guestId');
    
    if (!guestId) {
      const randomPart = Math.random().toString(36).substring(2, 15);
      const timestamp = Date.now().toString(36);
      guestId = `guest_${randomPart}_${timestamp}`;
      
      try {
        localStorage.setItem('guestId', guestId);
        console.log('[getGuestId] 降級方案新產生 guestId:', guestId);
      } catch (error) {
        console.error('[getGuestId] 儲存 guestId 到 localStorage 失敗:', error);
      }
    }
    
    return guestId;
  }
}

// 新增：YouTube videoId 萃取工具
export function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  
  try {
    // 🔧 修復：支援 YouTube 直播格式 /live/
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1]?.split(/[?&]/)[0] || null;
    }
    
    // 處理所有 youtube.com 格式，包括直播
    if (url.includes('youtube.com/')) {
      const urlObj = new URL(url);
      
      // 檢查是否是直播格式 /live/videoId
      if (url.includes('/live/')) {
        const pathParts = urlObj.pathname.split('/');
        const liveIndex = pathParts.indexOf('live');
        if (liveIndex !== -1 && pathParts[liveIndex + 1]) {
          return pathParts[liveIndex + 1].split('?')[0]; // 移除查詢參數
        }
      }
      
      // 檢查 v 參數（標準格式）
      const videoId = urlObj.searchParams.get('v');
      if (videoId) {
        return videoId;
      }
    }
    
    // 🔧 保留原有的正則表達式作為備用方案
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /youtu\.be\/([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    
    return null;
  } catch (error) {
    console.warn('extractYouTubeId 解析失敗:', error);
    return null;
  }
}

function stripDynamicFields(vbids: any[]): any[] {
  return vbids.map((vbid: any) => {
    const { timestamp, addedDate, ...rest } = vbid;
    return rest;
  });
}

export async function saveVbidsToFirestore({
  vbids,
  user,
  videoUrl,
  currentMode,
  numberKeys,
  mButtonState,
  youtubeInfo,
  checkpointCount,
  fileName,
  fileSize,
  version,
  fileRelativePath,
  documentId
}: {
  vbids: any[],
  user: any,
  videoUrl: string,
  currentMode: string,
  numberKeys: any,
  mButtonState: string,
  youtubeInfo?: any,
  checkpointCount?: number,
  fileName?: string,
  fileSize?: number,
  version?: string,
  fileRelativePath?: string,
  documentId?: string
}): Promise<string | null> {
  // if (!vbids || vbids.length === 0) return null; 移除，所有影片都會被匯入firestore 中

  // 🔧 修復：獲取檔案的修改日期作為發佈日期
  let fileAddedDate = new Date().toISOString();
  if (fileRelativePath && currentMode === 'VIDEO') {
    try {
      const { getFileModificationDate } = await import('./fileManager');
      fileAddedDate = await getFileModificationDate(fileRelativePath);
      console.log('📅 使用檔案修改日期作為發佈日期:', fileAddedDate);
    } catch (error) {
      console.log('⚠️ 無法獲取檔案修改日期，使用當前時間:', error);
    }
  }

  const cleanVbids = vbids.map(vbid => ({
    keyNo: Number(vbid.keyNo),
    timecode: vbid.timecode || '',
    note: vbid.note || '',
    screenshot: vbid.screenshot || '',
    flag: vbid.flag || 'on',
    timestamp: vbid.timestamp || Date.now(),
    frame: vbid.frame || 0,
    mediaMode: vbid.mediaMode || 'Unknown',
    playMode: vbid.playMode || 'M',
    isActive: vbid.isActive !== false // 確保 isActive 狀態正確
  }));

  // 🔧 修復：優先使用 fileRelativePath 而不是 blob URL
  let pathValue = '';
  if (fileRelativePath && fileRelativePath.trim() !== '') {
    // 對於本機檔案，使用相對路徑
    pathValue = fileRelativePath;
    console.log('[saveVbidsToFirestore] 使用檔案相對路徑:', pathValue);
  } else if (vbids[0] && vbids[0].path) {
    // 從 vbids 中獲取路徑
    pathValue = vbids[0].path;
    console.log('[saveVbidsToFirestore] 使用 vbids 路徑:', pathValue);
  } else if (videoUrl && !videoUrl.startsWith('blob:')) {
    // 如果不是 blob URL，使用 videoUrl
    pathValue = videoUrl;
    console.log('[saveVbidsToFirestore] 使用 videoUrl:', pathValue);
  } else {
    // 最後的後備方案：使用 videoUrl（可能是 blob URL）
    pathValue = videoUrl || '';
    console.log('[saveVbidsToFirestore] 使用後備 videoUrl:', pathValue);
  }
  
  if (!pathValue) return null;

  // 新增 videoId 欄位
  let videoId = extractYouTubeId(pathValue);
  
  // 🚨 關鍵修復：數據一致性驗證 - 防止數據污染
  // 檢查 numberKeys 中的 path 是否與當前 videoUrl 匹配
  if (numberKeys && Object.keys(numberKeys).length > 0) {
    const firstKey = Object.values(numberKeys)[0] as any;
    if (firstKey?.path) {
      const keyVideoId = extractYouTubeId(firstKey.path);
      const currentVideoId = videoId || extractYouTubeId(videoUrl);
      
      if (keyVideoId && currentVideoId && keyVideoId !== currentVideoId) {
        console.error('❌ 數據污染檢測：numberKeys 屬於不同的影片！');
        console.error('   當前影片 URL:', videoUrl);
        console.error('   當前影片 ID:', currentVideoId);
        console.error('   numberKeys 中的 path:', firstKey.path);
        console.error('   numberKeys 影片 ID:', keyVideoId);
        console.error('   🚨 阻止保存以防止數據污染！');
        
        // 拋出錯誤，阻止保存
        throw new Error(
          `數據不一致：嘗試將影片 ${keyVideoId} 的數據保存到影片 ${currentVideoId}。` +
          `這可能是由於影片切換時 state 未正確清空。請重新載入頁面。`
        );
      }
    }
  }
  
  // 🔧 關鍵修復：對於雲端檔案，優先使用原始檔名（fileName 參數）而不是從 GCS URL 提取
  // 這樣可以確保同一支影片在不同開啟方式下使用相同的 firestoreId
  if (!videoId && pathValue.includes('storage.googleapis.com')) {
    if (fileName && fileName.trim() !== '') {
      // ✅ 使用原始檔名生成 videoId
      const cleanFileName = fileName.replace(/[^a-zA-Z0-9]/g, '_');
      videoId = `file_${cleanFileName}`;
      console.log('[saveVbidsToFirestore] 使用原始檔名生成 videoId:', { fileName, videoId });
    } else {
      // ❌ 降級方案：從 GCS URL 提取文件名（可能是 UUID）
      const urlParts = pathValue.split('/');
      const urlFileName = urlParts[urlParts.length - 1];
      // 移除查詢參數
      videoId = urlFileName.split('?')[0];
      console.warn('[saveVbidsToFirestore] ⚠️ 警告：沒有提供原始檔名，從 GCS URL 提取（可能導致重複文檔）:', videoId);
    }
  }
  
  // 如果仍然沒有 videoId，使用 pathValue 但進行安全處理
  if (!videoId) {
    // 🔧 修復：對於其他 URL（雲端檔案），生成統一格式的 videoId
    // 新格式：file_[filename] （不再使用 safe_ 前綴）
    if (fileName && fileName.trim() !== '') {
      const cleanFileName = fileName.replace(/[^a-zA-Z0-9]/g, '_');
      videoId = `file_${cleanFileName}`;
      console.log('[saveVbidsToFirestore] 使用原始檔名生成 videoId:', videoId);
    } else {
      const cleanPath = pathValue.replace(/[^a-zA-Z0-9]/g, '_');
      videoId = `file_${cleanPath}`;
      console.log('[saveVbidsToFirestore] 生成雲端檔案 videoId (降級):', videoId);
    }
  }

  let numberKeysToSave = numberKeys;
  if (!numberKeysToSave || Object.keys(numberKeysToSave).length === 0) {
    numberKeysToSave = vbidsToNumberKeys(vbids);
  }

  // 🔍 調試：檢查 user 對象狀態
  console.log('[saveVbidsToFirestore] 調試用戶信息:', {
    user: user,
    userType: typeof user,
    hasUser: !!user,
    userUid: user?.uid,
    userEmail: user?.email,
    userUidType: typeof user?.uid,
    userUidExists: user?.uid !== undefined && user?.uid !== null,
    willUseGuestId: !user?.uid
  });

  // 🔧 修復：更嚴格的用戶ID檢查
  let userId: string;
  let isGuestUser: boolean;
  
  if (user && user.uid && typeof user.uid === 'string' && user.uid.trim() !== '') {
    // 用戶已登入且UID有效
    userId = user.uid;
    isGuestUser = false;
    console.log('[saveVbidsToFirestore] 使用登入用戶ID:', userId);
  } else {
    // 用戶未登入或UID無效，使用訪客ID
    userId = getGuestId();
    isGuestUser = true;
    console.log('[saveVbidsToFirestore] 使用訪客ID:', userId);
  }
  
  console.log('[saveVbidsToFirestore] 最終使用的 userId:', {
    userId,
    isGuestUser,
    source: isGuestUser ? 'getGuestId()' : 'user.uid'
  });

  // 🚀 新的分層設計：使用複合文檔ID
  let docRef = null;
  let existingData = null;
  let currentVersion = version || 'v1';
  
  // 🔧 首先檢查是否為其他用戶的文檔（需要協作模式）
  if (!isGuestUser) {
    try {
      // 🔧 修復：如果有 documentId，優先使用精確查詢
      if (documentId) {
        const specificDocRef = doc(db, "sharedVbids", documentId);
        const specificDocSnap = await getDoc(specificDocRef);
        
        if (specificDocSnap.exists()) {
          const data = specificDocSnap.data();
          if (data.userId === userId) {
            // 找到當前用戶的指定觀點文檔
            console.log('[saveVbidsToFirestore] 使用指定的觀點文檔:', documentId);
            docRef = specificDocRef;
            existingData = data;
          } else {
            // 🚨 安全修復：文檔屬於其他用戶，禁止修改
            console.error('[saveVbidsToFirestore] 安全錯誤：嘗試修改其他用戶的文檔', {
              documentId,
              documentOwner: data.userId,
              currentUser: userId,
              action: 'BLOCKED'
            });
            throw new Error('PERMISSION_DENIED: 您沒有權限修改此觀點，它屬於其他用戶');
          }
        } else {
          // 🔧 修復：文檔不存在時，創建新的觀點文檔（避免污染其他觀點）
          console.log('[saveVbidsToFirestore] 觀點文檔不存在，將創建新文檔:', documentId);
          docRef = specificDocRef;
          existingData = null;
        }
      }
      
      // 🔧 修復：只有在沒有提供 documentId 時，才使用 videoId 查詢
      if (!docRef && !documentId) {
        const otherUserQuery = query(
          collection(db, "sharedVbids"),
          where("videoId", "==", videoId)
        );
        const otherUserSnapshot = await getDocs(otherUserQuery);
        
        if (!otherUserSnapshot.empty) {
          // 優先查找當前用戶的文檔
          const currentUserDoc = otherUserSnapshot.docs.find(doc => doc.data().userId === userId);
          
          if (currentUserDoc) {
            // 找到當前用戶的文檔，直接使用
            console.log('[saveVbidsToFirestore] 找到當前用戶的文檔，直接使用:', currentUserDoc.id);
            docRef = currentUserDoc.ref;
            existingData = currentUserDoc.data();
          } else {
            // 🚨 安全修復：沒有找到當前用戶的文檔，禁止修改其他用戶的文檔
            const otherUserDocs = otherUserSnapshot.docs.filter(doc => doc.data().userId !== userId);
            if (otherUserDocs.length > 0) {
              console.error('[saveVbidsToFirestore] 安全錯誤：嘗試修改其他用戶的文檔', {
                videoId,
                currentUser: userId,
                otherUserDocs: otherUserDocs.map(doc => ({ id: doc.id, owner: doc.data().userId })),
                action: 'BLOCKED'
              });
              throw new Error('PERMISSION_DENIED: 您沒有權限修改此影片，它屬於其他用戶。請創建自己的觀點。');
            }
          }
        }
      }
    } catch (error) {
      // 如果是權限錯誤或協作模式錯誤，直接重新拋出
      if (error instanceof Error && (error.message === 'COLLABORATION_REQUIRED' || error.message.startsWith('PERMISSION_DENIED'))) {
        throw error;
      }
      console.warn('[saveVbidsToFirestore] 檢查其他用戶文檔失敗:', error);
    }
  }
  
  try {
    // 1. 對於已登入用戶，使用新的分層設計
    if (!isGuestUser && !docRef) {
      // 首先嘗試查找現有版本
      const q = query(
        collection(db, "sharedVbids"),
        where("userId", "==", userId),
        where("videoId", "==", videoId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // 找到現有記錄，優先使用最新版本而不是建立全新影片重點
        const existingDocs = querySnapshot.docs;
        
        // 按版本號排序，獲取最新版本
        const sortedDocs = existingDocs.sort((a, b) => {
          const versionA = a.id.match(/_v(\d+)$/)?.[1] || '0';
          const versionB = b.id.match(/_v(\d+)$/)?.[1] || '0';
          return parseInt(versionB) - parseInt(versionA);
        });
        
        const latestDoc = sortedDocs[0];
        const versionMatch = latestDoc.id.match(/_v(\d+)$/);
        currentVersion = versionMatch ? `v${versionMatch[1]}` : 'v1';
        
        // 使用最新版本的文檔
        docRef = latestDoc.ref;
        existingData = latestDoc.data();
        
        console.log('[saveVbidsToFirestore] 找到現有記錄，使用最新版本:', currentVersion);
      } else {
        // 如果沒有找到任何現有記錄，才生成新的複合文檔ID
        if (!docRef) {
          const compositeDocId = generateSafeDocId(videoId, userId, fileName, fileSize, currentVersion);
          docRef = doc(db, 'sharedVbids', compositeDocId);
          console.log('[saveVbidsToFirestore] 創建新文檔（分層設計）:', compositeDocId);
        }
      }
    } else {
      // 2. 對於訪客用戶，使用舊的邏輯（向後兼容）
      const directDocRef = doc(db, 'sharedVbids', videoId);
      const directDocSnap = await getDoc(directDocRef);
      
      if (directDocSnap.exists()) {
        docRef = directDocRef;
        existingData = directDocSnap.data();
        console.log('[saveVbidsToFirestore] 訪客用戶使用現有社群影片記錄:', videoId);
      }
    }
  } catch (error) {
    // 如果是協作模式錯誤，直接重新拋出
    if (error instanceof Error && error.message === 'COLLABORATION_REQUIRED') {
      throw error;
    }
    console.warn('[saveVbidsToFirestore] 查詢失敗，使用傳統查詢方式:', error);
  }

  // 2. 如果直接查詢沒有找到，對於訪客用戶，嘗試查找任何現有的社群影片記錄
  if (!docRef && isGuestUser) {
    try {
      const q = query(
        collection(db, "sharedVbids"),
        where("videoId", "==", videoId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // 找到現有記錄，使用第一個（優先使用有社群標記的）
        const existingDocs = querySnapshot.docs;
        const communityDoc = existingDocs.find(doc => 
          doc.data().zones && doc.data().zones.includes('community')
        ) || existingDocs[0];
        
        docRef = communityDoc.ref;
        existingData = communityDoc.data();
        console.log('[saveVbidsToFirestore] 訪客用戶找到現有社群影片記錄:', docRef.id);
      }
    } catch (error) {
      console.warn('[saveVbidsToFirestore] 訪客用戶查詢社群影片記錄失敗:', error);
    }
  }

  // 3. 如果還是沒有找到，對於已登入用戶使用傳統查詢方式
  if (!docRef && !isGuestUser) {
    const q = query(
      collection(db, "sharedVbids"),
      where("userId", "==", userId),
      where("videoId", "==", videoId)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      docRef = querySnapshot.docs[0].ref;
      existingData = querySnapshot.docs[0].data();
      console.log('[saveVbidsToFirestore] 找到現有文檔（使用傳統查詢）:', docRef.id);
    }
  }

  try {
    if (docRef && existingData) {
      // 已有記錄，做 update
      const existingVbids = existingData.vbids || [];
      
      // 如果 checkpointCount 為 0，完全清空 vbids
      let mergedVbids: any[] = [];
      if (checkpointCount === 0) {
        mergedVbids = [];
        console.log('[saveVbidsToFirestore] checkpointCount 為 0，完全清空 vbids');
      } else {
        // 刪除操作時，直接使用傳入的 vbids，不與現有數據合併
        // 這樣確保刪除操作後，Firestore 中的 vbids 與 UI 狀態完全一致
        mergedVbids = cleanVbids;
        
        console.log('[saveVbidsToFirestore] 刪除操作模式，直接使用傳入的 vbids:', {
          cleanVbidsLength: cleanVbids.length,
          checkpointCount,
          vbidsLength: vbids.length
        });
        
        // 確保 checkpointCount 與傳入的 vbids 數量一致（這些是當前活躍的檢查點）
        const actualCheckpointCount = vbids.length;
        if (actualCheckpointCount !== checkpointCount) {
          console.log(`[saveVbidsToFirestore] 修正 checkpointCount: ${checkpointCount} -> ${actualCheckpointCount}`);
          checkpointCount = actualCheckpointCount;
        }
      }

      const dataToUpdate = {
        // 🔧 改善：對於訪客用戶，不覆蓋現有的 userId，保持原創建者的身份
        ...(isGuestUser ? {} : { userId }),
        videoId,
        addedDate: fileAddedDate,
        modifiedAt: fileAddedDate, // 🔧 修復：設置 modifiedAt 為檔案修改日期
        mode: currentMode || 'YouTube',
        path: pathValue,
        privacy: 'Public',
        vbids: mergedVbids, // 使用合併後的 vbids
        numberKeys: numberKeysToSave,
        playMode: mButtonState,
        checkpointCount: checkpointCount || mergedVbids.length,
        ...(youtubeInfo ? { youtubeInfo } : {})
        // tags 不在這裡更新，以免覆蓋
      };
      await updateDoc(docRef, dataToUpdate);
      console.log('[saveVbidsToFirestore] Firestore 更新成功', docRef.id);
      
      // ✅ 統一使用 sharedVbids，移除 favorites 集合依賴
      console.log('[saveVbidsToFirestore] 已成功更新 sharedVbids 文檔:', docRef.id);
      
      // 🔧 關鍵修復：觸發數據更新事件，通知 ManagePages 刷新快取
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vbidsUpdated', {
          detail: {
            videoId: videoId,
            checkpointCount: checkpointCount || mergedVbids.length,
            documentId: docRef.id
          }
        }));
        console.log('[saveVbidsToFirestore] 已觸發 vbidsUpdated 事件');
      }
      
      return docRef.id;
    } else {
      // 🔧 改善：只有當影片完全不存在時才創建新記錄
      // 對於訪客用戶，這意味著這支影片還沒有被任何人記錄過
      // 🔥 新增：生成預設觀點名稱（使用 Nickname + 觀點）
      const defaultViewpointName = await (async () => {
        try {
          // 嘗試從 user 對象獲取 nickname 或 displayName
          const nickname = user?.nickname || user?.displayName || user?.email?.split('@')[0];
          if (nickname) {
            return `${nickname}觀點`;
          }
          
          // 如果沒有，從 Firestore users collection 查詢
          const { getDoc } = await import('firebase/firestore');
          const userDocRef = doc(db, 'users', userId);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            const userNickname = userData.nickname || userData.displayName || userData.email?.split('@')[0];
            return userNickname ? `${userNickname}觀點` : '預設觀點';
          }
          
          return '預設觀點';
        } catch (error) {
          console.warn('⚠️ 獲取用戶 Nickname 失敗，使用預設名稱:', error);
          return '預設觀點';
        }
      })();
      
      console.log(`📝 預設觀點名稱: ${defaultViewpointName}`);
      
      const dataToSave = {
        userId,
        videoId,
        viewpointName: defaultViewpointName,  // 🔥 新增：設置觀點名稱
        addedDate: fileAddedDate,
        modifiedAt: fileAddedDate, // 🔧 修復：設置 modifiedAt 為檔案修改日期
        mode: currentMode || 'YouTube',
        path: pathValue,
        privacy: 'Public',
        // 🔧 修復：訪客用戶預設使用 'community'，已登入用戶使用 'personal'
        zones: isGuestUser ? ['community'] : ['personal'],
        vbids: cleanVbids,
        numberKeys: numberKeysToSave,
        playMode: mButtonState,
        checkpointCount: checkpointCount || cleanVbids.length,
        tags: [],
        groupIds: [], // 🔧 修復：初始化 groupIds 字段，讓影片可以被分配到群組
        // 🔧 修復：初始化 readUsers，包含創建者自己
        readUsers: [userId],
        readCount: 1,
        lastReadAt: fileAddedDate,
        // 新增 YouTube 標籤支援
        ...(youtubeInfo ? { 
          youtubeInfo,
          youtubeHashtags: youtubeInfo.tags || [],
          youtubeMetadata: {
            hashtags: youtubeInfo.tags || [],
            title: youtubeInfo.title || '',
            description: youtubeInfo.description || '',
            channelTitle: youtubeInfo.channelTitle || '',
            publishedAt: youtubeInfo.publishedAt || '',
            viewCount: youtubeInfo.viewCount || '0',
            thumbnail: youtubeInfo.thumbnails?.high?.url || youtubeInfo.thumbnails?.medium?.url || '',
            duration: youtubeInfo.duration || '0:00'
          }
        } : {})
      };
      console.log('[saveVbidsToFirestore] 即將寫入 Firestore（使用安全的文檔 ID）', dataToSave);
      
      // 🚀 新的分層設計：生成複合文檔 ID
      const compositeDocId = generateSafeDocId(videoId, userId, fileName, fileSize, currentVersion);
      const newDocRef = doc(db, 'sharedVbids', compositeDocId);
      await setDoc(newDocRef, dataToSave);
      console.log('[saveVbidsToFirestore] Firestore 寫入成功（使用分層設計）', compositeDocId);
      
      // 🔧 關鍵修復：觸發數據更新事件，通知 ManagePages 刷新快取
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vbidsUpdated', {
          detail: {
            videoId: videoId,
            checkpointCount: checkpointCount || cleanVbids.length,
            documentId: compositeDocId
          }
        }));
        console.log('[saveVbidsToFirestore] 已觸發 vbidsUpdated 事件');
      }
      
      // 🔥 已移除：自動創建預設觀點的邏輯
      // 原因：會導致重複創建（剛創建 v1，又自動創建 v2）
      // 新策略：第一次保存的文檔就是預設觀點（v1），不需要額外創建
      console.log('✅ 新影片文檔已創建（預設觀點）:', compositeDocId);
      
      // *** 新增：初始化 videoStats 關注度為 1 ***
      try {
        const videoStatsRef = doc(db, 'videoStats', compositeDocId);
        const videoStatsSnap = await getDoc(videoStatsRef);
        
        if (!videoStatsSnap.exists()) {
          // 如果 videoStats 不存在，創建並設定關注度為 1
          await setDoc(videoStatsRef, {
            attentionCount: 1,
            userAttention: { [userId]: 1 },
            createdAt: new Date().toISOString()
          });
          console.log('[saveVbidsToFirestore] 已初始化 videoStats 關注度為 1:', compositeDocId);
        }
      } catch (e) {
        console.warn('[saveVbidsToFirestore] 初始化 videoStats 失敗:', e);
      }
      
      // ✅ 統一使用 sharedVbids，移除 favorites 集合依賴
      console.log('[saveVbidsToFirestore] 已成功保存到 sharedVbids 集合:', compositeDocId);
      
      // 🔧 新增：為創建影片的用戶自動創建觀看記錄
      try {
        const { updateUserWatchHistory } = await import('../services/userWatchHistoryService');
        await updateUserWatchHistory(userId, videoId, compositeDocId);
        console.log('✅ 已為創建者自動創建觀看記錄:', { userId, videoId, compositeDocId });
      } catch (error) {
        console.warn('⚠️ 創建觀看記錄失敗，但不影響主要流程:', error);
      }
      
      return compositeDocId; // 🚀 新的分層設計：返回複合文檔 ID
    }
  } catch (e) {
    alert('[saveVbidsToFirestore] Firestore 寫入失敗: ' + (e instanceof Error ? e.message : e));
    console.error('[saveVbidsToFirestore] Firestore 寫入失敗:', e, { userId, videoId, vbids: cleanVbids });
    throw e;
  }
} 

// 新增：查詢用戶的所有版本
export async function getUserVideoVersions(userId: string, videoId: string) {
  try {
    const q = query(
      collection(db, "sharedVbids"),
      where("userId", "==", userId),
      where("videoId", "==", videoId)
    );
    const querySnapshot = await getDocs(q);
    
    const versions = querySnapshot.docs.map(doc => ({
      documentId: doc.id,
      version: doc.id.match(/_v(\d+)$/)?.[1] || '1',
      data: doc.data(),
      createdAt: doc.data().addedDate
    }));
    
    // 按版本號排序
    versions.sort((a, b) => parseInt(a.version) - parseInt(b.version));
    
    console.log(`[getUserVideoVersions] 找到 ${versions.length} 個版本:`, versions.map(v => v.documentId));
    return versions;
  } catch (error) {
    console.error('[getUserVideoVersions] 查詢失敗:', error);
    throw error;
  }
}

// 新增：查詢特定版本
export async function getSpecificVersion(userId: string, videoId: string, version: string) {
  try {
    const compositeDocId = generateCompositeDocId(videoId, userId, version);
    const docRef = doc(db, 'sharedVbids', compositeDocId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        documentId: compositeDocId,
        version,
        data: docSnap.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('[getSpecificVersion] 查詢失敗:', error);
    throw error;
  }
}

// 新增：查詢最新版本
export async function getLatestVersion(userId: string, videoId: string) {
  try {
    const versions = await getUserVideoVersions(userId, videoId);
    
    if (versions.length === 0) {
      return null;
    }
    
    // 返回最新版本（最後一個）
    return versions[versions.length - 1];
  } catch (error) {
    console.error('[getLatestVersion] 查詢失敗:', error);
    throw error;
  }
}

// 新增：清理重複 vbids 並修正 checkpointCount
export async function cleanupDuplicateVbids(videoUrl: string, userId: string) {
  try {
    const videoId = extractYouTubeId(videoUrl) || videoUrl;
    
    // 查詢現有文檔
    const q = query(
      collection(db, "sharedVbids"),
      where("userId", "==", userId),
      where("videoId", "==", videoId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('[cleanupDuplicateVbids] 未找到文檔');
      return null;
    }
    
    const docRef = querySnapshot.docs[0].ref;
    const existingData = querySnapshot.docs[0].data();
    const existingVbids = existingData.vbids || [];
    
    console.log('[cleanupDuplicateVbids] 清理前 vbids 數量:', existingVbids.length);
    
    // 使用 keyNo 去重
    const vbidsMap = new Map();
    existingVbids.forEach((vbid: any) => {
      if (vbid.keyNo && vbid.isActive !== false) {
        vbidsMap.set(vbid.keyNo, vbid);
      }
    });
    
    const cleanedVbids = Array.from(vbidsMap.values());
    const actualCheckpointCount = cleanedVbids.length;
    
    console.log('[cleanupDuplicateVbids] 清理後 vbids 數量:', actualCheckpointCount);
    
    // 更新文檔
    await updateDoc(docRef, {
      vbids: cleanedVbids,
      checkpointCount: actualCheckpointCount
    });
    
    // ✅ 統一使用 sharedVbids，移除 favorites 集合依賴
    console.log('[cleanupDuplicateVbids] 已更新 sharedVbids checkpointCount:', actualCheckpointCount);
    
    console.log('[cleanupDuplicateVbids] 清理完成');
    return docRef.id;
  } catch (error) {
    console.error('[cleanupDuplicateVbids] 清理失敗:', error);
    throw error;
  }
}

// 版本管理相關函數

// 建立全新影片重點（重點歸零）
export async function createNewVersionReset(
  videoId: string, 
  userId: string, 
  fileName?: string, 
  fileSize?: number
): Promise<string> {
  try {
    console.log('[createNewVersionReset] 開始建立全新影片重點（重點歸零）:', { videoId, userId });
    
    // 獲取當前版本
    const currentVersions = await getUserVideoVersions(userId, videoId);
    const nextVersion = getNextVersion(currentVersions.map(v => v.version));
    
    // 生成新的複合文檔ID
    const newDocId = generateSafeDocId(videoId, userId, fileName, fileSize, nextVersion);
    
    // 創建新文檔，重點歸零
    const docRef = doc(db, 'sharedVbids', newDocId);
    const newData = {
      path: videoId,
      videoId: videoId,
      userId: userId,
      vbids: [], // 重點歸零
      checkpointCount: 0,
      addedDate: new Date().toISOString(),
      modifiedAt: new Date().toISOString(), // 🔧 修復：設置 modifiedAt
      lastOpenDate: new Date().toISOString(),
      mediaMode: 'YouTube',
      numberKeys: {},
      mButtonState: 'M',
      isNewVersion: true,
      parentVersion: currentVersions.length > 0 ? currentVersions[currentVersions.length - 1].version : null
    };
    
    await setDoc(docRef, newData);
    
    console.log('[createNewVersionReset] 新版本創建成功:', newDocId);
    return newDocId;
  } catch (error) {
    console.error('[createNewVersionReset] 創建失敗:', error);
    throw error;
  }
}

// 建立全新影片重點（保留重點）
export async function createNewVersionKeep(
  videoId: string, 
  userId: string, 
  fileName?: string, 
  fileSize?: number
): Promise<string> {
  try {
    console.log('[createNewVersionKeep] 開始建立全新影片重點（保留重點）:', { videoId, userId });
    
    // 獲取當前版本
    const currentVersions = await getUserVideoVersions(userId, videoId);
    const nextVersion = getNextVersion(currentVersions.map(v => v.version));
    
    // 獲取最新版本的數據
    const latestVersion = currentVersions.length > 0 ? currentVersions[currentVersions.length - 1] : null;
    
    // 生成新的複合文檔ID
    const newDocId = generateSafeDocId(videoId, userId, fileName, fileSize, nextVersion);
    
    // 創建新文檔，保留重點
    const docRef = doc(db, 'sharedVbids', newDocId);
    const newData = {
      path: videoId,
      videoId: videoId,
      userId: userId,
      vbids: latestVersion?.data.vbids || [], // 保留重點
      checkpointCount: latestVersion?.data.checkpointCount || 0,
      addedDate: new Date().toISOString(),
      modifiedAt: new Date().toISOString(), // 🔧 修復：設置 modifiedAt
      lastOpenDate: new Date().toISOString(),
      mediaMode: latestVersion?.data.mediaMode || 'YouTube',
      numberKeys: latestVersion?.data.numberKeys || {},
      mButtonState: latestVersion?.data.mButtonState || 'M',
      isNewVersion: true,
      parentVersion: latestVersion?.version || null
    };
    
    await setDoc(docRef, newData);
    
    console.log('[createNewVersionKeep] 新版本創建成功:', newDocId);
    return newDocId;
  } catch (error) {
    console.error('[createNewVersionKeep] 創建失敗:', error);
    throw error;
  }
}

// 開放協同作業
export async function enableCollaborativeMode(
  videoId: string, 
  userId: string, 
  version: string
): Promise<boolean> {
  try {
    console.log('[enableCollaborativeMode] 開始開放協同作業:', { videoId, userId, version });
    
    // 生成文檔ID
    const docId = generateCompositeDocId(videoId, userId, version);
    const docRef = doc(db, 'sharedVbids', docId);
    
    // 更新文檔，添加協同作業標記
    await updateDoc(docRef, {
      collaborativeMode: true,
      collaborativeEnabledBy: userId,
      collaborativeEnabledAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    });
    
    console.log('[enableCollaborativeMode] 協同作業已開放:', docId);
    return true;
  } catch (error) {
    console.error('[enableCollaborativeMode] 開放失敗:', error);
    throw error;
  }
}

// 檢查是否為協同作業模式
export async function isCollaborativeMode(
  videoId: string, 
  userId: string, 
  version: string
): Promise<boolean> {
  try {
    const docId = generateCompositeDocId(videoId, userId, version);
    const docRef = doc(db, 'sharedVbids', docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.collaborativeMode === true;
    }
    
    return false;
  } catch (error) {
    console.error('[isCollaborativeMode] 檢查失敗:', error);
    return false;
  }
}

// 獲取當前文檔的版本信息
export async function getCurrentVersionInfo(
  videoId: string, 
  userId: string
): Promise<{ currentVersion: string; nextVersion: string; isCollaborative: boolean } | null> {
  try {
    const versions = await getUserVideoVersions(userId, videoId);
    
    if (versions.length === 0) {
      return {
        currentVersion: 'v1',
        nextVersion: 'v2',
        isCollaborative: false
      };
    }
    
    const latestVersion = versions[versions.length - 1];
    const currentVersion = latestVersion.version;
    const nextVersion = getNextVersion(versions.map(v => v.version));
    const isCollaborative = latestVersion.data.collaborativeMode === true;
    
    return {
      currentVersion,
      nextVersion,
      isCollaborative
    };
  } catch (error) {
    console.error('[getCurrentVersionInfo] 獲取失敗:', error);
    return null;
  }
} 